import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ScrollText, Filter, Hash, Globe, Loader2, RefreshCw, X, Cpu, Timer, Lock, ShieldAlert, Download, Copy, ClipboardCheck, Trash2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { ActionBadge, Card, Pill, Section, DataTrace, traceFromAudit } from "./ui";

const ACTIONS = ["ALLOW", "WARN", "MASK", "BLOCK"];

export default function AuditLog() {
  const { session } = useAuth();
  const tid = session.tenant.id;
  const [params] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [decision, setDecision] = useState(params.get("decision") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [verification, setVerification] = useState(params.get("verification") || "");
  const [cats, setCats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("requests");

  const load = () => {
    setRows(null);
    api.audit(tid, {
      decision: decision || undefined,
      category: category || undefined,
      verification: verification || undefined,
    }).then(setRows);
  };

  useEffect(() => {
    Promise.all([api.sectorPacks(), api.customCategories(tid)]).then(([d, custom]) => {
      const s = session.tenant.sector;
      const pack = d.packs.find((p) => p.id === s);
      const builtin = pack ? pack.categories.map((c) => ({ id: c.id, label: c.label })) : [];
      const customOpts = (custom || []).map((c) => ({ id: c.id, label: `${c.label} (custom)` }));
      setCats([...builtin, ...customOpts]);
    });
  }, [tid, session.tenant.sector]);

  useEffect(load, [tid, decision, category, verification]);

  const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { hour12: false });

  const download = (filename, content, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportJson = () => download(`lucidgate-audit-${Date.now()}.json`, JSON.stringify(rows || [], null, 2), "application/json");
  const exportCsv = () => {
    const cols = ["timestamp", "user_name", "department_name", "decision", "provider_name",
      "provider_region", "latency_ms", "masked_count", "held", "audit_hash"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = [...cols, "categories"].join(",");
    const lines = (rows || []).map((r) =>
      [...cols.map((c) => esc(r[c])), esc((r.category_labels || []).join("; "))].join(","));
    download(`lucidgate-audit-${Date.now()}.csv`, [header, ...lines].join("\n"), "text/csv");
  };

  return (
    <Section
      title="Audit Log"
      subtitle="Every processed request — read-only and filterable. Isolated to this tenant."
      right={
        <div className="flex items-center gap-2">
          <div className="flex gap-1 rounded-lg border border-subtle bg-elevated/60 p-0.5">
            <button data-testid="tab-requests" onClick={() => setTab("requests")} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${tab === "requests" ? "bg-brand/15 text-brand" : "text-slate-400"}`}>Request Log</button>
            <button data-testid="tab-policy" onClick={() => setTab("policy")} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${tab === "policy" ? "bg-brand/15 text-brand" : "text-slate-400"}`}>Policy Activity</button>
          </div>
          {tab === "requests" && (
            <>
              <button data-testid="export-csv" onClick={exportCsv} disabled={!rows || rows.length === 0} className="flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 text-xs text-slate-300 hover:border-highlight disabled:opacity-40">
                <Download className="h-3.5 w-3.5" /> CSV
              </button>
              <button data-testid="export-json" onClick={exportJson} disabled={!rows || rows.length === 0} className="flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 text-xs text-slate-300 hover:border-highlight disabled:opacity-40">
                <Download className="h-3.5 w-3.5" /> JSON
              </button>
              <button data-testid="audit-refresh" onClick={load} className="flex items-center gap-2 rounded-lg border border-subtle px-3 py-1.5 text-xs text-slate-300 hover:border-highlight">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
            </>
          )}
        </div>
      }
    >
      {tab === "policy" ? (
        <PolicyActivity tid={tid} />
      ) : (
      <>
      <Card className="mb-4 flex flex-wrap items-center gap-3 p-3">
        <Filter className="h-4 w-4 text-slate-500" />
        <select data-testid="filter-decision" value={decision} onChange={(e) => setDecision(e.target.value)} className="rounded-lg border border-subtle bg-elevated px-3 py-1.5 text-xs outline-none focus:border-brand">
          <option value="">All decisions</option>
          {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-lg border border-subtle bg-elevated px-3 py-1.5 text-xs outline-none focus:border-brand">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select data-testid="filter-verification" value={verification} onChange={(e) => setVerification(e.target.value)} className="rounded-lg border border-subtle bg-elevated px-3 py-1.5 text-xs outline-none focus:border-brand">
          <option value="">All review states</option>
          <option value="verified">Human-verified</option>
          <option value="unverified">Unverified</option>
          <option value="not_applicable">No AI output</option>
        </select>
        {(decision || category || verification) && (
          <button onClick={() => { setDecision(""); setCategory(""); setVerification(""); }} className="text-xs text-slate-500 underline">Clear</button>
        )}
        <span className="ml-auto font-mono text-[11px] text-slate-600">{rows ? `${rows.length} record(s)` : ""}</span>
      </Card>

      {!rows ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <Card className="flex h-40 flex-col items-center justify-center text-slate-500">
          <ScrollText className="mb-2 h-6 w-6" /> No records yet — run some prompts through Gateway Chat.
        </Card>
      ) : (
        <Card className="overflow-x-auto custom-scrollbar">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-subtle bg-elevated/50 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3">Timestamp</th>
                <th className="px-4 py-3">User / Dept</th>
                <th className="px-4 py-3">Detected</th>
                <th className="px-4 py-3">Decision</th>
                <th className="px-4 py-3">Provider</th>
                <th className="px-4 py-3">Latency</th>
                <th className="px-4 py-3">Review</th>
                <th className="px-4 py-3">Hash</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid="audit-row" onClick={() => setSelected(r)} className="cursor-pointer border-b border-subtle/50 hover:bg-white/5">
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-400">{fmt(r.timestamp)}</td>
                  <td className="px-4 py-3">
                    <div className="text-white">{r.user_name}</div>
                    <div className="font-mono text-[10px] text-slate-500">{r.department_name || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex max-w-[220px] flex-wrap gap-1">
                      {r.category_labels.length ? r.category_labels.map((c) => (
                        <span key={c} className="rounded bg-warn/10 px-1.5 py-0.5 text-[10px] text-warn">{c}</span>
                      )) : <span className="text-[11px] text-allow">clean</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <ActionBadge action={r.decision} small />
                    {r.monitored && <span className="ml-1 text-[9px] uppercase text-mask">monitored</span>}
                  </td>
                  <td className="px-4 py-3">
                    {r.provider_name ? (
                      <div className="flex items-center gap-1.5">
                        {r.eu_sovereign_route && <Globe className="h-3 w-3 text-eu" />}
                        <span className="text-xs text-slate-300">{r.provider_name}</span>
                        {r.provider_simulated && <Pill tone="warn">sim</Pill>}
                      </div>
                    ) : <span className="text-xs text-slate-600">none</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-400">{r.latency_ms ? `${r.latency_ms} ms` : "—"}</td>
                  <td className="px-4 py-3"><VerifyBadge status={r.verification_status} /></td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 font-mono text-[10px] text-slate-500"><Hash className="h-3 w-3" />{r.audit_hash}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {selected && (
        <AuditDrawer
          row={selected}
          onClose={() => setSelected(null)}
          onDeleted={(id) => {
            setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
            setSelected(null);
          }}
        />
      )}
      </>
      )}
    </Section>
  );
}

function AuditDrawer({ row, onClose, onDeleted }) {
  const { session } = useAuth();
  const navigate = useNavigate();
  const isAdmin = session?.user?.role === "admin";
  const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { hour12: false });
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [delError, setDelError] = useState("");
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const traceText = () => {
    const t = traceFromAudit(row);
    const na = (step) => `not applicable — ${step.note}`;
    const sent = t.sent.applicable ? `${t.sent.text}${t.sent.unchanged ? "  [sent unchanged]" : ""}` : na(t.sent);
    const received = t.received.applicable ? t.received.text || "—" : na(t.received);
    let final = t.final.applicable ? t.final.text || "—" : na(t.final);
    if (t.final.applicable) {
      final += t.final.status === "verified"
        ? `\n\nVerification: Verified${t.final.verifiedBy ? ` by ${t.final.verifiedBy}` : ""}${t.final.verifiedAt ? ` · ${fmt(t.final.verifiedAt)}` : ""}`
        : "\n\nVerification: Unverified — not yet confirmed";
    }
    return [
      "LucidGate — Request trace",
      `Record: ${row.audit_hash} · ${fmt(row.timestamp)}`,
      `User / Department: ${row.user_name} · ${row.department_name || "—"}`,
      "",
      "STEP 1 — INPUT (prompt exactly as typed)",
      t.input || "—",
      "",
      "STEP 2 — SENT (payload the AI provider received)",
      sent,
      "",
      "STEP 3 — RECEIVED (raw AI response before verification gate)",
      received,
      "",
      "STEP 4 — FINAL OUTPUT (what the user can use)",
      final,
    ].join("\n");
  };

  const copyTrace = async () => {
    try {
      await navigator.clipboard.writeText(traceText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  const doDelete = async () => {
    setDelError("");
    setDeleting(true);
    try {
      const res = await api.deleteAudit(row.id, { user_id: session.user.id });
      if (res?.deleted) {
        onDeleted?.(row.id);
      } else {
        setDelError("Record could not be deleted.");
      }
    } catch (e) {
      setDelError(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="audit-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="lg-rise relative h-full w-full max-w-xl overflow-y-auto border-l border-subtle bg-surface p-6 shadow-2xl custom-scrollbar">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Audit record</div>
            <div className="flex items-center gap-2">
              <ActionBadge action={row.decision} />
              {row.monitored && <Pill tone="warn">monitored</Pill>}
              {row.held && <Pill tone="warn">held</Pill>}
            </div>
            <div className="mt-1 flex items-center gap-1.5 font-mono text-[11px] text-slate-500">
              <Hash className="h-3 w-3" /> {row.audit_hash} · {fmt(row.timestamp)}
            </div>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="rounded-lg border border-subtle p-1.5 text-slate-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            data-testid="continue-in-chat"
            onClick={() => { sessionStorage.setItem("lg_prefill", row.prompt_full || row.prompt_preview || ""); navigate("/chat"); }}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-[#04121c] hover:bg-sky-400"
          >
            Continue in Gateway Chat <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid="copy-full-trace"
            onClick={copyTrace}
            className="flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-brand/50 hover:text-brand"
          >
            {copied ? <ClipboardCheck className="h-3 w-3 text-allow" /> : <Copy className="h-3 w-3" />}
            {copied ? "Trace copied" : "Copy full trace"}
          </button>
          <button
            data-testid="export-record-json"
            onClick={() => {
              const blob = new Blob([JSON.stringify(row, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `lucidgate-audit-${row.audit_hash}.json`; a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-brand/50 hover:text-brand"
          >
            <Download className="h-3 w-3" /> Export this record (JSON)
          </button>
          {isAdmin && !confirmDelete && (
            <button
              data-testid="delete-record-btn"
              onClick={() => { setDelError(""); setConfirmDelete(true); }}
              className="flex items-center gap-1.5 rounded-lg border border-block/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-block hover:bg-block/10"
            >
              <Trash2 className="h-3 w-3" /> Delete record
            </button>
          )}
        </div>

        {isAdmin && confirmDelete && (
          <div data-testid="delete-confirm" className="mb-4 rounded-lg border border-block/40 bg-block/10 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-block">
              <AlertTriangle className="h-4 w-4" /> Permanently delete this record?
            </div>
            <p className="mt-1 text-xs text-slate-300">
              This removes the entire record — including the original prompt — from the database.
              This cannot be undone and breaks this entry from the audit trail. Use only to purge
              content submitted in error (e.g. real personal data).
            </p>
            {delError && (
              <div data-testid="delete-error" className="mt-2 rounded border border-block/40 bg-block/10 px-2 py-1 text-xs text-block">
                {delError}
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="confirm-delete-btn"
                onClick={doDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 rounded-lg bg-block px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-rose-500 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                {deleting ? "Deleting…" : "Confirm permanent delete"}
              </button>
              <button
                data-testid="cancel-delete-btn"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="rounded-lg border border-subtle px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-highlight disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <Field label="User / Department">
          {row.user_name} · {row.department_name || "—"}
        </Field>

        <div className="mb-4" data-testid="drawer-trace">
          <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <Lock className="h-3 w-3" /> Request trace · Input → Sent → Received → Final output
          </div>
          <DataTrace trace={traceFromAudit(row)} testid="audit-data-trace" />
        </div>

        <Field label="Detected entities">
          {(row.detected_entities || []).length === 0 ? (
            <span className="text-xs text-allow">No sensitive data detected.</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {row.detected_entities.map((e, i) => (
                <span key={i} className="rounded-md border border-subtle bg-elevated px-2 py-1 font-mono text-[11px] text-slate-300">
                  <span className="text-slate-500">{e.label}:</span> {e.matched_text}
                </span>
              ))}
            </div>
          )}
        </Field>

        <Field label="Layer resolution (Sector → Company → Department)">
          {(row.decisions || []).length === 0 ? (
            <span className="text-xs text-slate-500">No policy rules triggered.</span>
          ) : (
            <div className="space-y-2">
              {row.decisions.map((d) => (
                <div key={d.category} className="rounded-lg border border-subtle bg-elevated/60 p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white">{d.label}</span>
                    <ActionBadge action={d.applied_action} small />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    <span>sector {d.sector_default}</span><span className="text-slate-700">→</span>
                    <span>company {d.company_override || "—"}</span><span className="text-slate-700">→</span>
                    <span>dept {d.department_override || "—"}</span>
                    <span className="rounded px-1.5 py-0.5 text-eu" style={{ background: "rgba(59,130,246,0.12)" }}>by {d.deciding_layer}</span>
                    {d.sector_floor_enforced && <span className="text-warn">floor enforced</span>}
                    {d.monitored && <span className="text-mask">monitored</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Field>

        <Field label="Routing decision">
          <p className="text-xs text-slate-300">{row.routing_reason}</p>
          {row.provider_name ? (
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-subtle bg-elevated p-2.5">
              <Cpu className="h-4 w-4 text-brand" />
              <span className="text-sm text-white">{row.provider_name}</span>
              {row.eu_sovereign_route && <Pill tone="eu"><Globe className="h-3 w-3" /> EU</Pill>}
              {row.provider_simulated && <Pill tone="warn">sim</Pill>}
              <span className="ml-auto flex items-center gap-1 font-mono text-[11px] text-slate-500"><Timer className="h-3 w-3" />{row.latency_ms} ms</span>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-block/30 bg-block/10 p-2.5 text-xs text-block">
              <ShieldAlert className="h-4 w-4" /> No provider was contacted.
            </div>
          )}
        </Field>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="mb-4">
    <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
      <Lock className="h-3 w-3" /> {label}
    </div>
    {children}
  </div>
);

function VerifyBadge({ status }) {
  if (status === "verified")
    return <span data-testid="verify-badge-verified"><ActionBadge action="VERIFIED" small /></span>;
  if (status === "unverified")
    return <span data-testid="verify-badge-unverified"><ActionBadge action="UNVERIFIED" small /></span>;
  return <span className="font-mono text-[10px] text-slate-600">—</span>;
}


const EVENT_LABELS = { preset_change: "Posture preset", config_change: "Block-list config" };

function PolicyActivity({ tid }) {
  const [events, setEvents] = useState(null);
  const [evtType, setEvtType] = useState("");
  const [q, setQ] = useState("");
  const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { hour12: false });

  const load = () => { setEvents(null); api.policyEvents(tid).then(setEvents); };
  useEffect(load, [tid]);

  const filtered = (events || []).filter((e) =>
    (!evtType || e.event === evtType) &&
    (!q || (e.user_name || "").toLowerCase().includes(q.toLowerCase()) || (e.detail || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <>
      <Card className="mb-4 flex flex-wrap items-center gap-3 p-3">
        <Filter className="h-4 w-4 text-slate-500" />
        <select data-testid="pe-filter-type" value={evtType} onChange={(e) => setEvtType(e.target.value)} className="rounded-lg border border-subtle bg-elevated px-3 py-1.5 text-xs outline-none focus:border-brand">
          <option value="">All event types</option>
          <option value="preset_change">Posture preset</option>
          <option value="config_change">Block-list config</option>
        </select>
        <input data-testid="pe-filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search admin or detail…" className="rounded-lg border border-subtle bg-elevated px-3 py-1.5 text-xs outline-none focus:border-brand" />
        {(evtType || q) && <button onClick={() => { setEvtType(""); setQ(""); }} className="text-xs text-slate-500 underline">Clear</button>}
        <button data-testid="pe-refresh" onClick={load} className="ml-auto flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 text-xs text-slate-300 hover:border-highlight"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </Card>
      {!events ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : filtered.length === 0 ? (
        <Card className="flex h-40 flex-col items-center justify-center text-slate-500" data-testid="pe-empty">
          <ScrollText className="mb-2 h-6 w-6" /> No posture changes recorded yet — switch a preset in the Policy Builder.
        </Card>
      ) : (
        <Card className="overflow-x-auto custom-scrollbar">
          <table className="w-full min-w-[700px] text-left text-sm">
            <thead>
              <tr className="border-b border-subtle bg-elevated/50 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3">Timestamp</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Detail</th>
                <th className="px-4 py-3">Hash</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} data-testid="policy-event-row" className="border-b border-subtle/50">
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-400">{fmt(e.timestamp)}</td>
                  <td className="px-4 py-3 text-white">{e.user_name}</td>
                  <td className="px-4 py-3"><Pill tone={e.event === "preset_change" ? "eu" : "gold"}>{EVENT_LABELS[e.event] || e.event}</Pill></td>
                  <td className="px-4 py-3 text-[13px] text-slate-300">{e.detail}</td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1 font-mono text-[10px] text-slate-500"><Hash className="h-3 w-3" />{e.event_hash}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
